/**
 * Bottom sheet built on the shadcn Drawer (Base UI branch only — no Vaul props).
 * - Android/iOS Back closes the sheet (or goes to the previous step) via a history entry.
 * - Focus moves to the sheet title on open and on every step change.
 * - Height follows the visual viewport, so the on-screen keyboard never hides inputs.
 */
import { useEffect, useRef, type ReactNode } from 'react';
import { Drawer, DrawerContent, DrawerDescription, DrawerHeader, DrawerTitle } from './ui/drawer';
import { IconButton } from '@astryxdesign/core/IconButton';
import { HStack } from '@astryxdesign/core/HStack';
import { ArrowLeft, X } from '@phosphor-icons/react';
import { usePortalContainer } from '../app/tenant-context';

/**
 * While the sheet is open one extra history entry exists. Hardware/browser Back
 * calls `onBack`; if it returns true (handled: went one step back) the entry is
 * restored, otherwise the sheet closes. Closing from the UI removes the entry.
 */
export function useBackButton(open: boolean, onBack: () => boolean, onClose?: () => void) {
  const cb = useRef(onBack);
  cb.current = onBack;
  const close = useRef(onClose);
  close.current = onClose;
  useEffect(() => {
    if (!open) return;
    let active = true;
    history.pushState({ ...(history.state ?? {}), __sheet: true }, '');
    const onPop = () => {
      if (!active) return;
      if (cb.current()) history.pushState({ ...(history.state ?? {}), __sheet: true }, '');
      else {
        active = false;   // our entry is already gone: close without another history.back()
        close.current?.();
      }
    };
    window.addEventListener('popstate', onPop);
    return () => {
      window.removeEventListener('popstate', onPop);
      if (active && history.state?.__sheet) history.back();
    };
  }, [open]);
}

export function useKeyboardAware() {
  useEffect(() => {
    const vv = window.visualViewport;
    if (!vv) return;
    const update = () => {
      const kb = window.innerHeight - vv.height > 140;
      document.body.classList.toggle('keyboard-open', kb);
      document.documentElement.style.setProperty('--vvh', `${vv.height}px`);
    };
    update();
    vv.addEventListener('resize', update);
    return () => {
      vv.removeEventListener('resize', update);
      document.body.classList.remove('keyboard-open');
    };
  }, []);
}

export function Sheet(props: {
  open: boolean;
  onClose: () => void;
  title: string;
  description?: string;
  onBack?: () => void;
  stepKey?: string | number;
  footer?: ReactNode;
  children: ReactNode;
}) {
  const { open, onClose, title, description, onBack, stepKey, footer, children } = props;
  const titleRef = useRef<HTMLHeadingElement | null>(null);
  const portal = usePortalContainer();
  useKeyboardAware();

  useEffect(() => {
    if (!open) return;
    const t = window.setTimeout(() => titleRef.current?.focus({ preventScroll: true }), 60);
    return () => window.clearTimeout(t);
  }, [open, stepKey]);

  return (
    <Drawer open={open} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DrawerContent
        portalContainer={portal}
        className="max-w-[640px] mx-auto"
        style={{ maxHeight: 'min(calc(var(--vvh, 100dvh) - 24px), 92dvh)' }}
      >
        <DrawerHeader className="text-left group-data-[swipe-axis=y]/drawer-popup:text-left">
          <HStack gap={2} vAlign="center">
            {onBack ? <IconButton label="Назад" variant="ghost" icon={<ArrowLeft size={20} />} onClick={onBack} /> : null}
            <DrawerTitle ref={titleRef} tabIndex={-1} className="flex-1 text-lg font-semibold outline-none">{title}</DrawerTitle>
            <IconButton label="Закрыть" variant="ghost" icon={<X size={20} />} onClick={onClose} />
          </HStack>
          {description ? <DrawerDescription>{description}</DrawerDescription> : null}
        </DrawerHeader>
        <div className="sheet-scroll">{children}</div>
        {footer ? <div className="sheet-footer">{footer}</div> : null}
      </DrawerContent>
    </Drawer>
  );
}
